import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useFetcher } from 'react-router';

import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useBloomBasename } from '@jane/bloom/hooks/useBloomBasename';
import type { AutocompleteResponse } from '@jane/bloom/routes/$basename.api.search.autocomplete/types';
import { SEARCH_TERM_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';
import type { AlgoliaProduct } from '@jane/search/types';

interface RenderProps {
  products: SmartSortProduct<AlgoliaProduct>[];
  shouldFetchNextPage: boolean;
  totalProducts?: number;
}

interface SearchManagerProps {
  children: (args: RenderProps) => ReactNode;
  query: string;
}

export const SearchManager = ({ children, query }: SearchManagerProps) => {
  const fetcher = useFetcher<AutocompleteResponse>();
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView && query) {
        loadMore();
      }
    },
    skip: fetcher.state !== 'idle',
  });
  const basename = useBloomBasename();
  const searchResultsInstance = useMemoizedRowInstance(
    fetcher.data?.searchResultsInstance
  );

  const getSearchResultsInstance = useCallback(() => {
    return fetcher.load(
      `${basename}api/search/autocomplete?${new URLSearchParams({
        [SEARCH_TERM_QUERY_KEY]: query,
      })}`
    );
  }, [basename, fetcher, query]);

  const [hasMoreProducts, setHasMoreProducts] = useState(
    Boolean(searchResultsInstance?.hasNextPage())
  );
  const [products, setProducts] = useState(
    searchResultsInstance?.products ?? []
  );

  useEffect(() => {
    if (fetcher.data && searchResultsInstance) {
      setHasMoreProducts(searchResultsInstance.hasNextPage());
      setProducts(searchResultsInstance.products);
    }
  }, [fetcher.data, searchResultsInstance]);

  const loadMore = useCallback(async () => {
    if (!searchResultsInstance) {
      getSearchResultsInstance();
      return;
    }
    if (!hasMoreProducts) {
      return;
    }
    await searchResultsInstance.nextPage();
    setHasMoreProducts(searchResultsInstance.hasNextPage());
    setProducts([...searchResultsInstance.products]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSearchResultsInstance, hasMoreProducts, searchResultsInstance, query]);

  useEffect(() => {
    if (
      fetcher.state === 'idle' &&
      searchResultsInstance &&
      products.length === 0
    ) {
      setProducts([...searchResultsInstance.products]);
    }
  }, [fetcher.state, products.length, searchResultsInstance]);

  const payload: RenderProps = useMemo(
    () => ({
      products,
      shouldFetchNextPage: hasMoreProducts,
      totalProducts: searchResultsInstance?.totalProducts,
    }),
    [products, hasMoreProducts, searchResultsInstance?.totalProducts]
  );

  return (
    <>
      {children(payload)}
      <div ref={ref} aria-hidden="true" />
    </>
  );
};
